import clsx from 'clsx';
import { useRef, useState } from 'react';
import { Icon, Icons, Typography, TypographyType } from '../../../../components';
import {
  FOLDER_FILL_COLOR,
  FOLDER_STROKE_COLOR,
  ITEM_ACTIVE_BG_COLOR,
  ITEM_HOVER_BG_COLOR,
  ITEM_PASSIVE_BG_COLOR,
} from '../../../../constants';
import { useDetectTextOverflow, useGetUserPermissions } from '../../../../hooks';
import { ComponentSize, DropdownPlacement } from '../../../../types';
import ExplorerItemEndIcon from '../ExplorerItemEndIcon';
import { FolderActionType } from '../FolderActionModals';
import ExplorerFolderActions from './ExplorerFolderActions';

const MAX_LINES = 1;

interface ExplorerFolderItemProps {
  isLevelBeforeLast: boolean;
  isUsed: boolean;
  name: string;
  isOpen: boolean;
  openFolderActionModal: (type: FolderActionType) => void;
  toggleOpen: () => void;
}

const ExplorerFolderItem = ({
  isLevelBeforeLast,
  isUsed,
  name,
  isOpen,
  openFolderActionModal,
  toggleOpen,
}: ExplorerFolderItemProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const { canUserEditLibrary } = useGetUserPermissions();

  const nameRef = useRef<HTMLDivElement>(null);
  const isNameOverflowing = useDetectTextOverflow(nameRef, MAX_LINES);
  const nameTooltip = isNameOverflowing ? { content: name, placement: DropdownPlacement.BOTTOM } : undefined;

  return (
    <div
      className={clsx(
        'flex h-14 w-full cursor-pointer select-none items-center justify-between px-2 py-4',
        isOpen ? (isLevelBeforeLast ? ITEM_ACTIVE_BG_COLOR : ITEM_PASSIVE_BG_COLOR) : ITEM_HOVER_BG_COLOR
      )}
      onClick={toggleOpen}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center gap-2">
        <Icons
          icon={isOpen ? Icon.FOLDER_OPEN : Icon.FOLDER}
          size={ComponentSize.LARGE}
          fillColor={FOLDER_FILL_COLOR}
          color={FOLDER_STROKE_COLOR}
        />
        <Typography type={TypographyType.BASE} maxLines={MAX_LINES} ref={nameRef} tooltip={nameTooltip}>
          {name}
        </Typography>
      </div>
      <div className="flex items-center gap-2">
        {isHovered && canUserEditLibrary && (
          <ExplorerFolderActions
            isUsed={isUsed}
            onClose={() => setIsHovered(false)}
            openFolderActionModal={openFolderActionModal}
          />
        )}
        <ExplorerItemEndIcon icon={isOpen ? Icon.CHEVRON_LEFT : Icon.CHEVRON_RIGHT} onClick={toggleOpen} />
      </div>
    </div>
  );
};

export default ExplorerFolderItem;
